import { BasicConfig } from '@react-awesome-query-builder/ui';
import { bandToEsRange, DIR_FILTER_BASIC_FIELDS } from '../fields/basic_filter_fields';
import Icon from '../../utils/Icon.js';

const InitialConfig = BasicConfig;

const fields = { ...DIR_FILTER_BASIC_FIELDS }

const conjunctions = {
  ...InitialConfig.conjunctions
};

const operators = {
  ...InitialConfig.operators,
  equal: {
    ...InitialConfig.operators.equal,
    label: 'is',
    valueTypes: ['multiselect', 'number'],
    valueSources: ['value']
  },
  greater_or_equal: {
    ...InitialConfig.operators.greater_or_equal,
    label: 'is after',
    elasticSearchQueryType: 'range',
    valueTypes: ['date', 'number'],
  },
  less_or_equal: {
    ...InitialConfig.operators.less_or_equal,
    label: 'is before',
    elasticSearchQueryType: 'range',
    valueTypes: ['date', 'number'],
  },
  between: {
    ...InitialConfig.operators.between,
    label: 'is between',
    elasticSearchQueryType: 'range',
  },
  multiselect_equals: {
    ...InitialConfig.operators.multiselect_equals,
    label: 'is',
  },
  multiselect_not_equals: {
    ...InitialConfig.operators.multiselect_not_equals,
    label: 'is not',
  },
  multiselect_equals_range: {
    ...InitialConfig.operators.multiselect_equals,
    label: 'is',
    elasticSearchQueryType: 'range'
  },
  like: {
    ...InitialConfig.operators.like,
    label: 'contains',
    elasticSearchQueryType: 'match',
    valueTypes: ['text'],
  },
  starts_with: {
    ...InitialConfig.operators.starts_with,
    label: 'starts with',
    elasticSearchQueryType: 'match',
    valueTypes: ['text'],
  },
  select_any_in: {
    ...InitialConfig.operators.select_any_in,
  },
  has_no_value: {
    label: "is empty",
    valueTypes: ["text"],
    valueSources: ["value"],
    elasticSearchQueryType: "bool",
  },
  empty_date: {
    label: "is empty",
    valueTypes: ["date"],
    valueSources: ["value"],
    elasticSearchQueryType: "bool",
  }
};

// ✅ Custom widgets
const startsWithText = {
  ...InitialConfig.widgets.text,
  elasticSearchFormatValue: (queryType, value, op, fieldName, config) => {
    const query = Array.isArray(value) ? value[0] : value;
    return {
      [`${fieldName}.text_start`]: {
        query: query.toLowerCase(),
        operator: "and",
        boost: 10,
        analyzer: "searchkick_word_search"
      }
    }
  }
};

const hasNoDateWidget = {
  type: "date",
  valueSrc: "value",
  factory: () => null,
  elasticSearchFormatValue: (queryType, value, op, fieldName) => {
    return {
      "should": [
        {
          "bool": {
            "must_not": {
              "exists": { "field": fieldName }
            }
          }
        }
      ],
      "minimum_should_match": 1
    }
  }
}

const hasNoValueWidget = {
  type: "text",
  valueSrc: "value",
  factory: () => null,
  elasticSearchFormatValue: (queryType, value, op, fieldName) => ({
    "should": [
      {
        "bool": {
          "must_not": {
            "exists": { "field": fieldName }
          }
        }
      },
      {
        "term": {
          [fieldName]: ""
        }
      }
    ],
    "minimum_should_match": 1
  })
}

const likeTextWidget = {
  ...InitialConfig.widgets.text,
  elasticSearchFormatValue: (queryType, value, op, fieldName, config) => {
    const query = Array.isArray(value) ? value[0] : value;
    return {
      [`${fieldName}.word_middle`]: {
        query: query.toLowerCase(),
        operator: "and",
        boost: 10,
        analyzer: "searchkick_word_search"
      }
    }
  }
};

const monthYearToRangeWidget = {
  type: "date",
  valueSrc: "value",
  elasticSearchFormatValue: (queryType, value, op, fieldName) => {
    if (!value) return null;
    const v = Array.isArray(value) ? value[0] : value;
    if (!v) return null;

    const [year, month] = v.split("-").map(Number);
    if (!year || !month || month < 1 || month > 12) return null;
    if (op === "greater_or_equal") {
      let nextMonth = month === 12 ? 1 : month + 1;
      let nextYear = month === 12 ? year + 1 : year;
      const dateStr = `${nextYear}-${String(nextMonth).padStart(2, "0")}-01`;
      return { [fieldName]: { gte: dateStr } };
    }
    if (op === "less_or_equal") {
      let previousMonth = month === 1 ? 12 : month - 1;
      let previousYear = month === 1 ? year - 1 : year;
      const dateStr = `${previousYear}-${String(previousMonth).padStart(2, "0")}-01`;
      return { [fieldName]: { lte: dateStr } };
    }
    return null;
  }
}

const experienceWidget = {
  ...InitialConfig.widgets.multiselect,
  elasticSearchFormatValue: (queryType, value, op, fieldName, config) => {
    const band = Array.isArray(value) ? value[0] : value;
    return bandToEsRange(fieldName, band);
  }
};

// Keep in sync with backend FilterBandRanges["job_change"] (filter_band_ranges.rb) — presence aggs + saved-report parsing mirror these ranges.
const jobChangeWidget = {
  ...InitialConfig.widgets.multiselect,
  elasticSearchFormatValue: (queryType, value, op, fieldName, config) => {
    const checked = Array.isArray(value) ? value : [value];
    const clauses = [];
    if (checked.includes("employment")) {
      clauses.push({
        recent_job_changed_at: { gte: "now-90d/d" }
      });
    }
    if (checked.includes("designation")) {
      clauses.push({
        recent_designation_changed_at: { gte: "now-90d/d" }
      });
    }
    if (clauses.length === 1) return clauses[0];
    if (clauses.length > 1) {
      return {
        bool: {
          should: clauses,
          minimum_should_match: 1
        }
      };
    }
    return null;
  }
}

const widgets = {
  ...InitialConfig.widgets,
  date: {
    ...InitialConfig.widgets.date,
    valueFormat: "YYYY-MM-DD",
  },
  multiselect: {
    ...InitialConfig.widgets.multiselect,
  },
  startsWithText,
  likeTextWidget,
  experienceWidget,
  jobChangeWidget,
  hasNoValueWidget,
  monthYearToRangeWidget,
  hasNoDateWidget
}

const types = {
  ...InitialConfig.types
}

const localeSettings = {

}
const settings = {
  ...InitialConfig.settings,
  ...localeSettings,
}
const funcs = {};

const ctx = InitialConfig.ctx;

/**
 * Builds a query-builder config around a field dictionary.
 *
 * Everything except `fields` — operators, widgets, types, settings — is shared
 * across pages, so a second page supplies only its own field set.
 */
export const makeBasicFilterConfig = (customFields = fields, { trackingPrefix = "Directory" } = {}) => ({
  ctx,
  // Names the Mixpanel events so a second page does not report as Directory.
  trackingPrefix,
  conjunctions,
  operators,
  widgets,
  types,
  settings,
  fields: customFields,
  funcs
});

/** Directory's config. Kept as the default export shape for existing importers. */
export const basic_filters_config = makeBasicFilterConfig();
